import { Children, forwardRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { cn } from "@ui/lib/utils";

interface MessagesListProps {
  /**
   * - `top`: stack from the top.
   * - `bottom`: stack from the bottom; top clips when content overflows.
   * - `center`: center vertically while content fits; bottom-anchor and clip the top once content overflows.
   */
  anchor?: "top" | "bottom" | "center";
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  style?: CSSProperties;
  /**
   * Splits children into two cells with a fixed-height gap between. Children `[0, atIndex)` render in the
   * top cell (bottom-anchored, so the newest "graduated" row sits just above the gap). Children
   * `[atIndex, end)` render in the bottom cell using the requested `anchor` (center / bottom). The outer
   * `overflow-hidden` and `style` (mask) remain on the wrapping container so top-fade still works.
   * `atIndex` is clamped to 0 internally.
   */
  split?: { atIndex: number; gapPx?: number };
}

export const MessagesList = forwardRef<HTMLDivElement, MessagesListProps>(
  ({ anchor = "top", children, className, contentClassName, style, split }, ref) => {
    if (split) {
      const childArray = Children.toArray(children);
      const safeIndex = Math.max(0, Math.min(split.atIndex, childArray.length));
      const topChildren = childArray.slice(0, safeIndex);
      const bottomChildren = childArray.slice(safeIndex);
      const gapPx = split.gapPx ?? 24;
      return (
        <div
          ref={ref}
          style={style}
          className={cn("tw-relative tw-size-full tw-min-h-0 tw-overflow-hidden", className)}
        >
          <div className='tw-absolute tw-inset-0 tw-grid' style={{ gridTemplateRows: `1fr ${gapPx}px 1fr` }}>
            <div className={cn("tw-flex tw-min-w-0 tw-flex-col tw-justify-end tw-gap-2", contentClassName)}>
              {topChildren}
            </div>
            <div aria-hidden />
            <div
              className={cn(
                "tw-flex tw-min-w-0 tw-flex-col tw-gap-2",
                anchor === "top" ? "tw-justify-start" : "tw-justify-end",
                contentClassName,
              )}
            >
              {anchor === "center" ? (
                <div className='tw-my-auto tw-flex tw-w-full tw-flex-col tw-gap-2'>{bottomChildren}</div>
              ) : (
                bottomChildren
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div ref={ref} style={style} className={cn("tw-relative tw-size-full tw-min-h-0 tw-overflow-hidden", className)}>
        <div
          className={cn(
            "tw-absolute tw-inset-x-0 tw-bottom-0 tw-flex tw-min-h-full tw-w-full tw-flex-col tw-gap-2",
            anchor === "top" ? "tw-justify-start" : "tw-justify-end",
            contentClassName,
          )}
        >
          {anchor === "center" ? (
            <div className='tw-my-auto tw-flex tw-w-full tw-flex-col tw-gap-2'>{children}</div>
          ) : (
            children
          )}
        </div>
      </div>
    );
  },
);

MessagesList.displayName = "MessagesList";
